import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, conflictsOf, errMsg } from "@/lib/api";
import { useUrlFilters } from "@/lib/urlfilters";
import { addDays, hhmm, todayStr } from "@/lib/format";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const EMPTY_SHIFT = { site_id: "", post_id: "", date: "", shift_type: "day", start_time: "07:00", end_time: "19:00", guard_id: "", notes: "" };

export default function Schedule() {
  const [weekOffset, setWeekOffset] = useState(0);
  const [siteFilter, setSiteFilter] = useState("all");
  const [sites, setSites] = useState([]);
  const [posts, setPosts] = useState([]);
  const [guards, setGuards] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [exceptions, setExceptions] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_SHIFT);
  const [conflicts, setConflicts] = useState(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [offerFor, setOfferFor] = useState(null);
  const [eligible, setEligible] = useState([]);
  const [callOffFor, setCallOffFor] = useState(null);
  const [callOffReason, setCallOffReason] = useState("");
  const [tplOpen, setTplOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tplForm, setTplForm] = useState({ template_id: "", from_date: "", to_date: "" });
  const [tplNewOpen, setTplNewOpen] = useState(false);
  const [tplNew, setTplNew] = useState({ name: "", site_id: "", entries: [] });

  // ⚠️ THE RANGE IS STATE, NOT A DERIVATION OF weekOffset. It used to be
  // `useMemo(() => addDays(today, weekOffset * 7))`, which means a link can only
  // ever land on a whole week starting today — and "posts nobody stood in the
  // last 30 days" is not a week. The week buttons now SET the range; a link can
  // set it to anything.
  const [range, setRange] = useUrlFilters({ from: "", to: "", status: "all" });
  const from = range.from || addDays(todayStr(), weekOffset * 7);
  const to = range.to || addDays(from, 6);

  const load = () => {
    const params = `from_date=${from}&to_date=${to}${siteFilter !== "all" ? `&site_id=${siteFilter}` : ""}`;
    api.get(`/shifts?${params}`).then((r) => setShifts(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/schedule/exceptions").then((r) => setExceptions(r.data)).catch(() => {});
  };
  useEffect(load, [from, to, siteFilter]);

  // A week button leaves an explicit range behind and goes back to the weekly
  // view, or the arrows would appear to do nothing.
  const stepWeek = (d) => { setRange({ from: "", to: "" }); setWeekOffset((w) => w + d); };
  useEffect(() => {
    api.get("/sites").then((r) => setSites(r.data)).catch(() => {});
    api.get("/posts").then((r) => setPosts(r.data)).catch(() => {});
    api.get("/guards").then((r) => setGuards(r.data)).catch(() => {});
    api.get("/roster-templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, []);

  const sitePosts = posts.filter((p) => p.site_id === form.site_id);
  const shown = range.status === "all" ? shifts : shifts.filter((x) => x.status === range.status);

  const createShift = async (withOverride = false) => {
    try {
      await api.post("/shifts", {
        ...form,
        post_id: form.post_id || null,
        guard_id: form.guard_id || null,
        override_reason: withOverride ? overrideReason : null,
      });
      toast.success("Shift created");
      setCreateOpen(false);
      setConflicts(null);
      setOverrideReason("");
      setForm(EMPTY_SHIFT);
      load();
    } catch (e) {
      const c = conflictsOf(e);
      if (c) {
        setConflicts(c);
        toast.error("Conflicts detected — override requires a reason");
      } else {
        toast.error(errMsg(e));
      }
    }
  };

  const publish = async (s) => {
    try {
      await api.post(`/shifts/${s.id}/publish`);
      toast.success(`Published ${s.ref_no}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openOffer = async (s) => {
    setOfferFor(s);
    try {
      const { data } = await api.get(`/shifts/eligible?date=${s.date}&start_time=${s.start_time}&end_time=${s.end_time}&site_id=${s.site_id}`);
      setEligible(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const sendOffer = async (guardId) => {
    try {
      await api.post(`/shifts/${offerFor.id}/offer`, { guard_id: guardId });
      toast.success("Offer sent — guard notified");
      setOfferFor(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const submitCallOff = async () => {
    try {
      const { data } = await api.post(`/shifts/${callOffFor.id}/call-off`, { reason: callOffReason });
      toast.success(`Called off. Replacement shift ${data.replacement_shift.ref_no} opened.`);
      setCallOffFor(null);
      setCallOffReason("");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const applyTemplate = async () => {
    try {
      const { data } = await api.post(`/roster-templates/${tplForm.template_id}/apply`, { from_date: tplForm.from_date, to_date: tplForm.to_date });
      toast.success(`${data.created} open shifts generated`);
      setTplOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  // Entry defaults come from the POST's own requirements, never a constant here:
  // the weekday headcount a site is contracted for already lives on the post,
  // and re-deriving it in the UI is how a template comes to propose a roster the
  // contract does not pay for.
  const entriesForSite = (siteId) =>
    posts.filter((p) => p.site_id === siteId && p.active !== false).flatMap((p) =>
      [["day", "07:00", "19:00"], ["night", "19:00", "07:00"]].map(([kind, st, en]) => ({
        post_id: p.id,
        post_name: p.name,
        shift_type: kind,
        start_time: st,
        end_time: en,
        headcount: (p.requirements?.weekday || {})[kind] ?? 0,
        days: [0, 1, 2, 3, 4, 5, 6],
      }))
    );

  const setEntry = (i, patch) =>
    setTplNew((t) => ({ ...t, entries: t.entries.map((e, j) => (j === i ? { ...e, ...patch } : e)) }));

  const toggleDay = (i, wd) =>
    setEntry(i, {
      days: tplNew.entries[i].days.includes(wd)
        ? tplNew.entries[i].days.filter((d) => d !== wd)
        : [...tplNew.entries[i].days, wd].sort((a, b) => a - b),
    });

  const openNewTemplate = () => {
    const first = sites[0]?.id || "";
    setTplNew({ name: "", site_id: first, entries: first ? entriesForSite(first) : [] });
    setTplNewOpen(true);
  };

  const createTemplate = async () => {
    try {
      // Rows the operator zeroed out are dropped rather than sent as headcount
      // 0: apply_template creates nothing for them anyway, and a template
      // listing posts it never staffs reads as broken.
      const entries = tplNew.entries
        .filter((e) => e.headcount > 0 && e.days.length > 0)
        .map(({ post_name, ...e }) => e);
      const { data } = await api.post("/roster-templates", { name: tplNew.name.trim(), site_id: tplNew.site_id, entries });
      setTemplates((t) => [...t, data]);
      setTplNewOpen(false);
      setTplForm((f) => ({ ...f, template_id: data.id }));
      setTplOpen(true);
      toast.success(`Template saved \u2014 ${entries.length} post/shift row${entries.length === 1 ? "" : "s"}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="schedule-page">
      <PageHeader title="Scheduling & Deployment" sub={`${from} → ${to}`}
        actions={
          <>
            <Button variant="outline" className="rounded-none" data-testid="week-prev" onClick={() => setWeekOffset(weekOffset - 1)}>← Week</Button>
            <Button variant="outline" className="rounded-none" data-testid="week-today" onClick={() => { setRange({ from: "", to: "" }); setWeekOffset(0); }}>Today</Button>
            <Button variant="outline" className="rounded-none" data-testid="week-next" onClick={() => setWeekOffset(weekOffset + 1)}>Week →</Button>
            <Button variant="outline" className="rounded-none" data-testid="apply-template-button" onClick={() => setTplOpen(true)}>Apply template</Button>
            <Button className="rounded-none" data-testid="new-shift-button" onClick={() => { setCreateOpen(true); setConflicts(null); }}>New shift</Button>
          </>
        } />

      {exceptions && (exceptions.open_shifts.length + exceptions.unacknowledged.length + exceptions.missed.length > 0) && (
        <div className="border border-[hsl(var(--alert))] bg-[hsl(var(--alert))]/5 p-3 mb-4 text-sm" data-testid="exceptions-strip">
          <span className="font-bold uppercase text-xs tracking-wider">Exceptions:</span>{" "}
          <span className="font-mono text-xs">{exceptions.open_shifts.length} open · {exceptions.unacknowledged.length} unacknowledged · {exceptions.missed.length} missed (7d)</span>
        </div>
      )}

      <div className="mb-3 w-64">
        <Select value={siteFilter} onValueChange={setSiteFilter}>
          <SelectTrigger data-testid="site-filter" className="rounded-none"><SelectValue placeholder="All sites" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All sites</SelectItem>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
        {/* Reachable from the dashboard's OPEN SHIFTS tile, which lands here
            with ?status=open already applied. */}
        <Select value={range.status} onValueChange={(v) => setRange({ status: v })}>
          <SelectTrigger data-testid="status-filter" className="rounded-none w-44">
            <SelectValue placeholder="Any status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any status</SelectItem>
            <SelectItem value="open">Open (unfilled)</SelectItem>
            <SelectItem value="offered">Offered</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="in_progress">In progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="called_off">Called off</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {range.from && (
        <div className="border border-border p-3 mb-3 text-xs text-muted-foreground flex flex-wrap items-center gap-2"
             data-testid="schedule-range-banner">
          <span>
            Showing <span className="font-mono text-foreground">{from} → {to}</span>
            {range.status !== "all" && <> · status <span className="font-mono text-foreground">{range.status}</span></>}
            {" "}— set by the link you followed, not the week view.
          </span>
          <Button size="sm" variant="outline" className="rounded-none h-7 text-[11px]"
                  data-testid="schedule-back-to-week"
                  onClick={() => { setRange({ from: "", to: "", status: "all" }); setWeekOffset(0); }}>
            Back to this week
          </Button>
        </div>
      )}

      <DataTable testid="shifts-table" rows={shown} empty="No shifts in this range"
        columns={[
          { key: "date", label: "Date", render: (r) => <div><div className="font-mono text-xs font-bold">{r.date}</div><div className="font-mono text-[10px] text-muted-foreground">{r.ref_no}</div></div> },
          { key: "time", label: "Shift", render: (r) => <div><div className="font-mono text-xs">{hhmm(r.start_time)}–{hhmm(r.end_time)}</div><StatusBadge value={r.shift_type} /></div> },
          { key: "site_name", label: "Site / Post", render: (r) => <div><div className="font-semibold">{r.site_name}</div><div className="text-xs text-muted-foreground">{r.post_name || "—"}</div></div> },
          { key: "guard", label: "Guard", render: (r) => r.guard_name ? <div><div>{r.guard_name}</div>{r.acknowledged ? <span className="font-mono text-[10px] text-[hsl(var(--success))]">ACK</span> : r.status === "published" ? <span className="font-mono text-[10px] text-[hsl(var(--warning))]">NO ACK</span> : null}</div> : <span className="font-mono text-xs text-[hsl(var(--warning))]">OPEN</span> },
          { key: "status", label: "Status", render: (r) => <div><StatusBadge value={r.status} />{r.override && <div className="font-mono text-[9px] text-[hsl(var(--alert))] mt-1">OVERRIDE</div>}</div> },
          {
            key: "actions", label: "Actions", render: (r) => (
              <div className="flex flex-wrap gap-1">
                {r.status === "draft" && r.guard_id && <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" data-testid={`publish-${r.ref_no}`} onClick={() => publish(r)}>Publish</Button>}
                {(r.status === "open" || r.status === "draft") && <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" data-testid={`offer-${r.ref_no}`} onClick={() => openOffer(r)}>Offer…</Button>}
                {["published", "accepted", "in_progress"].includes(r.status) && <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" data-testid={`calloff-${r.ref_no}`} onClick={() => setCallOffFor(r)}>Call-off</Button>}
              </div>
            ),
          },
        ]} />

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-none max-h-[85vh] overflow-auto" data-testid="new-shift-dialog">
          <DialogHeader><DialogTitle className="font-heading">New shift</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider font-bold">Site</Label>
              <Select value={form.site_id} onValueChange={(v) => setForm({ ...form, site_id: v, post_id: "" })}>
                <SelectTrigger data-testid="shift-site" className="rounded-none mt-1"><SelectValue placeholder="Select site" /></SelectTrigger>
                <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Post</Label>
              <Select value={form.post_id} onValueChange={(v) => setForm({ ...form, post_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="shift-post" className="rounded-none mt-1"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Any</SelectItem>{sitePosts.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">Date</Label><Input data-testid="shift-date" type="date" className="rounded-none mt-1" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Type</Label>
              <Select value={form.shift_type} onValueChange={(v) => setForm({ ...form, shift_type: v, start_time: v === "day" ? "07:00" : "19:00", end_time: v === "day" ? "19:00" : "07:00" })}>
                <SelectTrigger data-testid="shift-type" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="day">Day</SelectItem><SelectItem value="night">Night</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs uppercase tracking-wider font-bold">Start</Label><Input data-testid="shift-start" type="time" className="rounded-none mt-1" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></div>
              <div><Label className="text-xs uppercase tracking-wider font-bold">End</Label><Input data-testid="shift-end" type="time" className="rounded-none mt-1" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} /></div>
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider font-bold">Guard (blank = open shift)</Label>
              <Select value={form.guard_id} onValueChange={(v) => setForm({ ...form, guard_id: v === "open" ? "" : v })}>
                <SelectTrigger data-testid="shift-guard" className="rounded-none mt-1"><SelectValue placeholder="Open shift" /></SelectTrigger>
                <SelectContent><SelectItem value="open">Open shift</SelectItem>{guards.map((g) => <SelectItem key={g.id} value={g.id}>{g.name} · {g.employee_no}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {conflicts && (
            <div className="border border-[hsl(var(--alert))] bg-[hsl(var(--alert))]/5 p-3 mt-3" data-testid="conflict-panel">
              <div className="text-xs font-bold uppercase tracking-wider mb-1">Conflicts detected</div>
              <ul className="text-xs list-disc pl-4 mb-2">{conflicts.map((c, i) => <li key={i}>{c}</li>)}</ul>
              <Label className="text-xs uppercase tracking-wider font-bold">Override reason (required, audited)</Label>
              <Textarea data-testid="override-reason" className="rounded-none mt-1" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} />
              <Button data-testid="override-submit" variant="outline" className="rounded-none mt-2 w-full" onClick={() => createShift(true)} disabled={!overrideReason.trim()}>
                Assign anyway with override
              </Button>
            </div>
          )}
          <Button data-testid="shift-create-submit" className="w-full rounded-none mt-3" onClick={() => createShift(false)} disabled={!form.site_id || !form.date}>Create shift</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!offerFor} onOpenChange={() => setOfferFor(null)}>
        <DialogContent className="rounded-none max-w-lg max-h-[85vh] overflow-auto" data-testid="offer-dialog">
          <DialogHeader><DialogTitle className="font-heading">Offer {offerFor?.ref_no} — {offerFor?.date} {hhmm(offerFor?.start_time)}–{hhmm(offerFor?.end_time)}</DialogTitle></DialogHeader>
          <div className="space-y-1">
            {eligible.map((g) => (
              <div key={g.guard_id} className="flex items-center justify-between border border-border p-2" data-testid={`eligible-${g.employee_no}`}>
                <div>
                  <div className="text-sm font-semibold">{g.name}</div>
                  <div className="font-mono text-[10px] text-muted-foreground">{g.employee_no} · {g.branch_name}</div>
                  {g.conflicts.length > 0 && <div className="text-[10px] text-[hsl(var(--alert))]">{g.conflicts.join("; ")}</div>}
                </div>
                <Button size="sm" className="rounded-none" data-testid={`offer-to-${g.employee_no}`} disabled={g.conflicts.length > 0} onClick={() => sendOffer(g.guard_id)}>Offer</Button>
              </div>
            ))}
            {eligible.length === 0 && <div className="font-mono text-xs text-muted-foreground">No active guards found</div>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!callOffFor} onOpenChange={() => setCallOffFor(null)}>
        <DialogContent className="rounded-none" data-testid="calloff-dialog">
          <DialogHeader><DialogTitle className="font-heading">Call-off — {callOffFor?.guard_name} · {callOffFor?.date}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground mb-2">This cancels the shift, opens a replacement shift, notifies dispatchers, and logs a client notice if the contract requires it.</p>
          <Label className="text-xs uppercase tracking-wider font-bold">Reason (required, audited)</Label>
          <Textarea data-testid="calloff-reason" className="rounded-none mt-1" value={callOffReason} onChange={(e) => setCallOffReason(e.target.value)} />
          <Button data-testid="calloff-submit" className="w-full rounded-none mt-3" onClick={submitCallOff} disabled={!callOffReason.trim()}>Confirm call-off</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="rounded-none" data-testid="template-dialog">
          <DialogHeader><DialogTitle className="font-heading">Apply roster template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Template</Label>
              <Select value={tplForm.template_id} onValueChange={(v) => setTplForm({ ...tplForm, template_id: v })}>
                <SelectTrigger data-testid="template-select" className="rounded-none mt-1"><SelectValue placeholder="Select template" /></SelectTrigger>
                <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
              {templates.length === 0 && (
                /* This line used to read "create one via API POST
                   /api/roster-templates". A screen that tells its user to reach
                   for curl is a feature with no door -- and this is the one tool
                   that refills a whole week of roster at once, so the app was
                   sending people to the manual path for the bulk operation. */
                <p className="font-mono text-[10px] text-muted-foreground mt-1">
                  No templates yet &mdash; <button type="button" data-testid="template-new-inline"
                    className="underline font-bold" onClick={() => { setTplOpen(false); openNewTemplate(); }}>create one</button>.
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider font-bold">From</Label><Input data-testid="template-from" type="date" className="rounded-none mt-1" value={tplForm.from_date} onChange={(e) => setTplForm({ ...tplForm, from_date: e.target.value })} /></div>
              <div><Label className="text-xs uppercase tracking-wider font-bold">To</Label><Input data-testid="template-to" type="date" className="rounded-none mt-1" value={tplForm.to_date} onChange={(e) => setTplForm({ ...tplForm, to_date: e.target.value })} /></div>
            </div>
            <Button data-testid="template-apply-submit" className="w-full rounded-none" onClick={applyTemplate} disabled={!tplForm.template_id || !tplForm.from_date || !tplForm.to_date}>Generate open shifts</Button>
            <button type="button" data-testid="template-new" className="w-full font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground underline"
              onClick={() => { setTplOpen(false); openNewTemplate(); }}>New template</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create a roster template. Entries are per post, per shift, with the
          weekdays it runs and the headcount the post needs -- exactly the shape
          POST /roster-templates already accepted and nothing could produce.
          Defaults are READ FROM THE POST'S OWN requirements, so the headcount a
          template proposes is the headcount the site is contracted for rather
          than a number somebody typed here. */}
      <Dialog open={tplNewOpen} onOpenChange={setTplNewOpen}>
        <DialogContent className="rounded-none max-w-2xl" data-testid="template-new-dialog">
          <DialogHeader><DialogTitle className="font-heading">New roster template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold">Name</Label>
                <Input data-testid="template-name" className="rounded-none mt-1" placeholder="Standard week"
                  value={tplNew.name} onChange={(e) => setTplNew({ ...tplNew, name: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold">Site</Label>
                <Select value={tplNew.site_id} onValueChange={(v) => setTplNew({ ...tplNew, site_id: v, entries: entriesForSite(v) })}>
                  <SelectTrigger data-testid="template-site" className="rounded-none mt-1"><SelectValue placeholder="Select site" /></SelectTrigger>
                  <SelectContent>{sites.map((st) => <SelectItem key={st.id} value={st.id}>{st.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {tplNew.entries.length > 0 && (
              <div className="border border-border">
                <div className="grid grid-cols-[1fr_auto_auto_1fr] gap-2 px-3 py-2 border-b border-border font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  <span>Post / shift</span><span>Hours</span><span>Heads</span><span>Days</span>
                </div>
                {tplNew.entries.map((en, i) => (
                  <div key={i} className="grid grid-cols-[1fr_auto_auto_1fr] gap-2 px-3 py-2 items-center border-b border-border last:border-b-0">
                    <div>
                      <div className="font-semibold text-xs">{en.post_name}</div>
                      <div className="font-mono text-[10px] uppercase text-muted-foreground">{en.shift_type}</div>
                    </div>
                    <span className="font-mono text-[10px]">{en.start_time}&ndash;{en.end_time}</span>
                    <Input type="number" min="0" max="20" data-testid={`template-heads-${i}`}
                      className="rounded-none h-7 w-14 font-mono text-xs" value={en.headcount}
                      onChange={(e) => setEntry(i, { headcount: Math.max(0, parseInt(e.target.value || "0", 10)) })} />
                    <div className="flex gap-0.5">
                      {["M", "T", "W", "T", "F", "S", "S"].map((dl, wd) => (
                        <button key={wd} type="button" data-testid={`template-day-${i}-${wd}`}
                          onClick={() => toggleDay(i, wd)}
                          className={`h-6 w-6 border font-mono text-[10px] ${en.days.includes(wd)
                            ? "bg-foreground text-background border-foreground"
                            : "border-border text-muted-foreground"}`}>{dl}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {tplNew.site_id && tplNew.entries.length === 0 && (
              <p className="font-mono text-[10px] text-muted-foreground">That site has no active posts, so there is nothing to roster.</p>
            )}

            <Button data-testid="template-create-submit" className="w-full rounded-none" onClick={createTemplate}
              disabled={!tplNew.name.trim() || !tplNew.site_id || !tplNew.entries.some((e) => e.headcount > 0 && e.days.length > 0)}>
              Save template
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
